import React, { useEffect, useState } from "react";
import { getStyles } from "../styles";
import { LookupUserDTOV1, SiteDTO, UserRole, PasswordResetAuditLog } from "@beable/roster-backend-client-v1";
import ChangeLanguageModal from "../Modals/ChangeLanguageModal";
import { Button, Container, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ResetPasswordModal from "../Modals/ResetPasswordModal";
import ImpersonateUserModal from "../Modals/ImpersonateUserModal";
import userSupportService from "../../../services/UserSupportService";
import InfoIcon from "@mui/icons-material/Info";
import moment from "moment";
import ContactInfoModal from "../Modals/ContactInfoModal";
import ResetAssessmentModal from "../Modals/ResetAssessmentModal";

interface UserToolingProps {
    user: LookupUserDTOV1;
    userRole: string | null;
    site: SiteDTO;
    onLanguageChange: ((languageCode: string) => void);
  }
  
export default function UserTooling({
    user,
    userRole, 
    site,
    onLanguageChange
}: UserToolingProps) {
    const theme = useTheme();
    const styles = getStyles(theme);

    const [openCLModal, setOpenCLModal] = useState(false);
    const [openPswdModal, setOpenPswdModal] = useState(false);
    const [openContactInfoModal, setOpenContactInfoModal] = useState(false);
    const [openImpersonateModal, setOpenImpersonateModal] = useState(false);
    const [openAssessmentModal, setOpenAssessmentModal] = useState(false);
    const [isStudent, setIsStudent] = useState(true); 
    const [passwordAuditLog, setPasswordAuditLog] = useState<PasswordResetAuditLog>();

    const closeCLModal = (language: string | undefined) => {
        if (language) {
            onLanguageChange(language);
        }
        setOpenCLModal(false);
    }

    const closePswdModal = () => {
        setOpenPswdModal(false);
    }

    const closeAssessmentModal = () => {
        setOpenAssessmentModal(false);
    }

    const closeContactInfoModal = () => {
        setOpenContactInfoModal(false);
    }

    const closeImpersonateModal = () => {
        setOpenImpersonateModal(false);
    }

    const isUserAccount = !!user.userAccountId;

    useEffect(() => {
        const getUserInfo = () => {
            setIsStudent(userRole === UserRole.student);
        }

        const getPasswordAuditLogData = async () => {
            const log = await userSupportService.getLastPasswordResetAuditLog(
                site.id,
                user.userId
            );
            if (log) {
                setPasswordAuditLog(log);
            }
        }

        getUserInfo();
        getPasswordAuditLogData()
    }, [user, site, userRole]);


    const resetButtonDisabled = () => {
       return isStudent || !site.allowPasswordChange; 
    }

    const disabledBtnMessage = () => {
        if (!site.allowPasswordChange) {
            return "Users in this district cannot have their passwords reset by Beable. Look in Creatio for hints on how to find their passwords or get help from district personnel.";
        }

        if (isStudent) {
            return "Beable cannot reset passwords for students. Instruct the student to see their teacher who can reset the password for them.";
        }

        return "";
    }

    const passwordResetTooltip = () => {
        if (!passwordAuditLog) {
            return '';
        }

        const resetBy = passwordAuditLog.setByUser ? 
            (` by ${passwordAuditLog.setByUser.firstName ?
                `${passwordAuditLog.setByUser.firstName} ${passwordAuditLog.setByUser.lastName}` :
                passwordAuditLog.setByUser?.username}`
            ) : '';

        return passwordAuditLog !== undefined ?
         `Password reset${resetBy} on ${moment(
            passwordAuditLog.setAt
        ).format("DD-MMM-YYYY")}` :
        '';
    };

    return (
        <Container sx={styles.userActionButtons}>
        <Button
            variant="contained"
            color="primary"
            aria-label="change-language"
            onClick={() => setOpenCLModal(true)}
            data-testid="btn-change-application-language"
            id="btn-change-application-language"
            sx={styles.verticalBtn}
            disabled={!isUserAccount}
        >
            Change Application Language
        </Button>

        <br />
        <Button
            variant="contained"
            color="primary"
            aria-label="impersonate-user"
            onClick={() => setOpenImpersonateModal(true)}
            data-testid="btn-impersonate-user"
            id="btn-impersonate-user"
            sx={styles.verticalBtn}
            disabled={!isUserAccount}
        >
            Impersonate User
        </Button>

        {/* Disable for RGT-1035 */}
        {/* <Button
            variant="contained"
            color="primary"
            aria-label="change-password"
            onClick={() => setOpenCPwordModal(true)}
            data-testid="btn-change-password"
            id="btn-change-password"
        >
            Change Password
        </Button> */}
        
        <br />
        <Tooltip title={disabledBtnMessage()}>
          <span>
            <Button
                variant="contained"
                color="primary"
                aria-label="reset-password"
                onClick={() => setOpenPswdModal(true)}
                data-testid="btn-reset-password"
                id="btn-reset-password"
                sx={styles.verticalBtn}
                disabled={resetButtonDisabled()}
            >
                Reset Password
            </Button>
          </span>
        </Tooltip>
        {passwordAuditLog && (
            <Tooltip title={passwordResetTooltip()} sx={styles.infoIcon}>
              <InfoIcon color="primary" aria-label={"Audit Log"}></InfoIcon>
            </Tooltip>
          )}
        <br />
        { isUserAccount && (
            <Button
                variant="contained"
                color="primary"
                aria-label="view-update-emails"
                onClick={() => setOpenContactInfoModal(true)}
                data-testid="btn-view-update-emails"
                id="btn-view-update-emails"
                sx={styles.verticalBtn}
            >
                View/Update email address
            </Button>
        )}
        <br />
        { isUserAccount && isStudent && (
            <Button
                variant="contained"
                color="primary"
                aria-label="view-reset-assessment"
                onClick={() => setOpenAssessmentModal(true)}
                data-testid="btn-view-reset-assessment"
                id="btn-view-reset-assessment"
                sx={styles.verticalBtn}
            >
                Reset Assessment
            </Button>
        )}
        
        { isUserAccount && (<ChangeLanguageModal open={openCLModal} handleClose={closeCLModal} applicationLanguage={user.applicationLanguage!} userAccountId={user.id} userId={user.userId} siteId={user.siteId} />) }
        <ResetPasswordModal open={openPswdModal} handleClose={closePswdModal} profile={user} userRole={userRole}/>
        <ContactInfoModal open={openContactInfoModal} handleClose={closeContactInfoModal} profile={user}/>
        <ImpersonateUserModal open={openImpersonateModal} handleClose={closeImpersonateModal} profile={user} />
        <ResetAssessmentModal open={openAssessmentModal} handleClose={closeAssessmentModal} profile={user} />
        </Container>

    )
}  